import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  CircularProgress,
  Alert,
  Pagination,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Work as WorkIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  Link as LinkIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { format } from 'date-fns';
import apiClient from '../../services/api';

// Format date with seconds in IST timezone
const formatDateTimeWithSeconds = (dateString) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    // Format in IST timezone with seconds
    return date.toLocaleString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  } catch (error) {
    return '—';
  }
};

// Use env var if set, otherwise use relative URL (works for same-domain backend)
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // In production (ondigitalocean.app or services.knls.in), use relative URL
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  // Development fallback
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

// Centralized auth header builder to avoid Bearer null
const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const categories = [
  { value: 'ssc', label: 'SSC' },
  { value: 'railway', label: 'Railway' },
  { value: 'bank', label: 'Bank' },
  { value: 'teaching', label: 'Teaching' },
  { value: 'police', label: 'Police' },
  { value: 'other', label: 'Other' },
];

const qualifications = [
  { value: 'SSC', label: 'SSC' },
  { value: 'Intermediate', label: 'Intermediate' },
  { value: 'ITI', label: 'ITI' },
  { value: 'Degree', label: 'Degree' },
  { value: 'B.Tech', label: 'B.Tech' },
  { value: 'M.Tech', label: 'M.Tech' },
];

const departments = [
  { value: 'rrb', label: 'RRB' },
  { value: 'uppsc', label: 'UPPSC' },
  { value: 'postal', label: 'Postal' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'police', label: 'Police' },
  { value: 'bank', label: 'Bank' },
];

// Validation schema for CREATE (strict - all mandatory fields required)
const createValidationSchema = yup.object({
  notificationName: yup.string().required('Notification name is required'),
  organization: yup.string().nullable(),
  category: yup.string().nullable(),
  lastDate: yup.date().nullable(),
});

// Validation schema for UPDATE (lenient - no mandatory fields for partial updates)
const updateValidationSchema = yup.object({
  notificationName: yup.string().nullable(),
  organization: yup.string().nullable(),
  category: yup.string().nullable(),
  lastDate: yup.date().nullable(),
});

const JobAlertsManagement = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('active');
  const [qualificationFilter, setQualificationFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialog, setViewDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [editingAlert, setEditingAlert] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedAlerts, setSelectedAlerts] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { data, isLoading, error, refetch } = useQuery(
    ['job-alerts', page, search, categoryFilter, activeFilter, qualificationFilter, departmentFilter],
    async () => {
      const params = {
        limit: 10,
        offset: (page - 1) * 10,
        search,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        isActive: activeFilter === 'all' ? 'any' : activeFilter === 'active' ? 'true' : activeFilter === 'flagged' ? 'any' : 'false',
        includeDeleted: activeFilter === 'flagged' ? 'true' : 'false',
        qualification: qualificationFilter !== 'all' ? qualificationFilter : undefined,
        filterKey: departmentFilter !== 'all' ? departmentFilter : undefined,
      };
      // Remove undefined params
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      const response = await apiClient.get(`/jobalerts`, { params });
      return response.data;
    },
    {
      refetchOnWindowFocus: true,
      refetchInterval: 30000, // Auto-refresh every 30 seconds
      keepPreviousData: true,
    }
  );

  const { data: stats } = useQuery(
    'job-alerts-stats',
    async () => {
      const response = await apiClient.get(`/jobalerts/stats/summary`);
      return response.data.data;
    }
  );

  const createMutation = useMutation(
    async (values) => {
      const response = await apiClient.post(`/jobalerts`, values);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Job alert created successfully');
        queryClient.invalidateQueries('job-alerts');
        queryClient.invalidateQueries('job-alerts-stats');
        formik.resetForm();
        setDialogOpen(false);
        setEditingAlert(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create job alert');
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, ...values }) => {
      const response = await apiClient.put(`/jobalerts/${id}`, values);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data?.message || 'Job alert updated successfully!');
        queryClient.invalidateQueries('job-alerts');
        queryClient.invalidateQueries('job-alerts-stats');
        formik.resetForm();
        setDialogOpen(false);
        setEditingAlert(null);
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to update job alert';
        toast.error(errorMessage);
        console.error('Update error:', error.response?.data || error);
      },
    }
  );

  // Reset selection when search/filter changes
  useEffect(() => {
    setSelectedAlerts(new Set());
    setSelectAll(false);
  }, [search, categoryFilter, activeFilter, qualificationFilter, departmentFilter]);

  const handleSelectAlert = (alertId) => {
    const idString = String(alertId);
    const newSelected = new Set(selectedAlerts);
    if (newSelected.has(idString)) {
      newSelected.delete(idString);
      console.log(`🔲 Deselected alert: ${idString}`);
    } else {
      newSelected.add(idString);
      console.log(`☑️ Selected alert: ${idString}`);
    }
    setSelectedAlerts(newSelected);
    setSelectAll(newSelected.size === data?.data?.length && data?.data?.length > 0);
    console.log(`📊 Selection updated: ${newSelected.size} selected`);
  };

  const fetchAllMatchingAlertIds = async () => {
    try {
      // Use the exact same filtering logic as the main query
      const params = {
        limit: 10000, // Large limit to get all matching records
        offset: 0,
        search,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        isActive: activeFilter === 'all' ? 'any' : activeFilter === 'active' ? 'true' : activeFilter === 'flagged' ? 'any' : 'false',
        includeDeleted: activeFilter === 'flagged' ? 'true' : 'false',
        qualification: qualificationFilter !== 'all' ? qualificationFilter : undefined,
        filterKey: departmentFilter !== 'all' ? departmentFilter : undefined,
      };
      // Remove undefined params (same as main query)
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      console.log('🔍 Fetching all matching alerts with params:', params);
      
      // Fetch all matching alerts without pagination to get all IDs
      const response = await apiClient.get(`/jobalerts`, { params });

      console.log('📡 Fetch response received:', {
        status: response.status,
        hasData: !!response.data,
        success: response.data?.success,
        dataType: Array.isArray(response.data) ? 'array' : typeof response.data,
        dataKeys: response.data ? Object.keys(response.data) : []
      });

      // Handle both response.data.success and direct data array
      const alertsData = response.data.success !== false 
        ? (response.data.data || (Array.isArray(response.data) ? response.data : []) || [])
        : [];
      
      console.log(`📊 Parsed ${alertsData.length} alerts from response`);
      
      const ids = alertsData.map(alert => {
        const id = alert.id;
        if (!id) {
          console.warn('⚠️ Alert missing ID:', alert);
          return null;
        }
        const idString = String(id);
        console.log(`📌 Found alert ID: ${idString} - ${alert.notificationName || 'N/A'}`);
        return idString;
      }).filter(id => id !== null && id !== 'undefined' && id !== 'null');
      
      console.log(`✅ Fetched ${ids.length} matching alert IDs from ${alertsData.length} alerts`);
      console.log(`📋 Sample IDs:`, ids.slice(0, 5));
      return ids;
    } catch (err) {
      console.error('Error fetching all matching alert IDs:', err);
      toast.error('Failed to fetch all matching alerts');
      return [];
    }
  };

  const handleSelectAll = async () => {
    if (selectAll) {
      // Deselect all
      console.log('🔲 Deselecting all alerts');
      setSelectedAlerts(new Set());
      setSelectAll(false);
    } else {
      // Select all matching alerts (across all pages)
      console.log('☑️ Selecting all matching alerts...');
      const allMatchingIds = await fetchAllMatchingAlertIds();
      console.log(`📊 Select All: Found ${allMatchingIds.length} matching IDs`);
      if (allMatchingIds.length > 0) {
        const newSet = new Set(allMatchingIds);
        setSelectedAlerts(newSet);
        setSelectAll(true);
        console.log(`✅ Selected ${allMatchingIds.length} alerts. Set size: ${newSet.size}`);
        toast.info(`Selected ${allMatchingIds.length} alert(s) matching your search/filter`);
      } else {
        console.warn('⚠️ No alerts found to select');
        toast.warning('No alerts found to select');
      }
    }
  };

  const handleDeleteClick = (alert) => {
    if (alert) {
      // Single delete
      setDeleteDialog(alert);
    } else if (selectedAlerts.size > 0) {
      // Bulk delete
      setDeleteDialog({ bulk: true, count: selectedAlerts.size });
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      let idsToDelete = [];

      if (deleteDialog?.bulk) {
        // Bulk delete - get IDs from selectedAlerts Set
        idsToDelete = Array.from(selectedAlerts);
        console.log('Bulk delete - Selected alerts Set:', selectedAlerts);
        console.log('Bulk delete - IDs to delete:', idsToDelete);
      } else if (deleteDialog?.id) {
        // Single delete
        idsToDelete = [deleteDialog.id];
      }

      if (idsToDelete.length === 0) {
        toast.error('No alerts selected for deletion');
        console.warn('No IDs to delete. Selected alerts:', selectedAlerts);
        setDeleting(false);
        return;
      }

      // Ensure all IDs are valid (not null/undefined) and convert to string for API
      const validIds = idsToDelete
        .map(id => {
          // Handle both number and string IDs
          if (id === null || id === undefined) {
            return null;
          }
          const idValue = String(id).trim();
          return idValue && idValue !== 'undefined' && idValue !== 'null' && idValue !== '' ? idValue : null;
        })
        .filter(id => id !== null);

      if (validIds.length === 0) {
        toast.error('No valid alert IDs found for deletion');
        console.error('Invalid IDs received:', idsToDelete);
        setDeleting(false);
        return;
      }

      console.log(`Deleting ${validIds.length} alerts (from ${idsToDelete.length} selected) with IDs:`, validIds);
      console.log('Selected alerts Set:', Array.from(selectedAlerts));

      // Delete all selected alerts sequentially
      let successful = 0;
      let failed = 0;
      const errors = [];

      // If deleting from flagged list, use permanent delete. Otherwise, move to flagged.
      const isFlaggedList = activeFilter === 'flagged';
      
      for (let i = 0; i < validIds.length; i++) {
        const id = validIds[i];
        try {
          console.log(`${isFlaggedList ? 'Permanently deleting' : 'Moving to flagged'} alert ${i + 1}/${validIds.length}: ID ${id}`);
          
          let response;
          if (isFlaggedList) {
            // Permanent delete from flagged list
            response = await apiClient.delete(`/jobalerts/${id}?hardDelete=true`);
          } else {
            // Move to flagged (soft delete)
            response = await apiClient.patch(`/jobalerts/${id}/move-to-flagged`, {});
          }
          
          console.log(`Response for ID ${id}:`, response.data);
          
          if (response.data && response.data.success) {
            successful++;
            console.log(`✓ Successfully ${isFlaggedList ? 'deleted' : 'moved to flagged'} alert ID ${id}`);
          } else {
            failed++;
            const errorMsg = response.data?.message || 'Unknown error';
            errors.push(`ID ${id}: ${errorMsg}`);
            console.warn(`✗ Failed to ${isFlaggedList ? 'delete' : 'move'} alert ID ${id}:`, errorMsg);
          }
        } catch (err) {
          failed++;
          const errorMsg = err.response?.data?.message || err.message || 'Unknown error';
          errors.push(`ID ${id}: ${errorMsg}`);
          console.error(`✗ Error ${isFlaggedList ? 'deleting' : 'moving'} alert ID ${id}:`, err);
          console.error('Error details:', {
            status: err.response?.status,
            data: err.response?.data,
            message: err.message
          });
        }
        
        // Small delay between operations
        if (i < validIds.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      console.log(`Deletion complete: ${successful} successful, ${failed} failed`);
      
      const actionText = isFlaggedList ? 'deleted' : 'moved to flagged';
      
      if (successful > 0) {
        // Show alert with count
        const message = `${successful} alert(s) ${actionText} successfully${failed > 0 ? `, ${failed} failed` : ''}`;
        toast.success(message, {
          autoClose: 5000,
        });
        
        // Show browser alert with detailed count
        window.alert(`✅ Success!\n\n${successful} alert(s) ${actionText} successfully${failed > 0 ? `\n${failed} alert(s) failed` : ''}`);
        
        if (failed > 0 && errors.length > 0) {
          console.error('Operation errors:', errors);
          toast.warning(`Some operations failed. Check console for details.`);
        }
        setDeleteDialog(null);
        setSelectedAlerts(new Set());
        setSelectAll(false);
        // Refresh the alerts list
        queryClient.invalidateQueries('job-alerts');
        queryClient.invalidateQueries('job-alerts-stats');
        // Force refetch after a short delay
        setTimeout(() => {
          queryClient.refetchQueries('job-alerts');
          queryClient.refetchQueries('job-alerts-stats');
        }, 500);
      } else {
        const errorMessage = `Failed to ${isFlaggedList ? 'delete' : 'move'} all alerts. Errors: ${errors.slice(0, 3).join(', ')}${errors.length > 3 ? '...' : ''}`;
        toast.error(errorMessage);
        window.alert(`❌ Error!\n\nFailed to ${isFlaggedList ? 'delete' : 'move'} alerts.\n\nErrors: ${errors.slice(0, 5).join('\n')}${errors.length > 5 ? '\n...' : ''}`);
        console.error('All operations failed. Full error list:', errors);
      }
    } catch (err) {
      console.error('Error deleting alerts:', err);
      toast.error(err.response?.data?.message || 'Failed to delete alerts');
    } finally {
      setDeleting(false);
    }
  };

  const deleteMutation = useMutation(
    async ({ id, hardDelete = false }) => {
      const url = hardDelete 
        ? `/jobalerts/${id}?hardDelete=true`
        : `/jobalerts/${id}/move-to-flagged`;
      
      const method = hardDelete ? 'delete' : 'patch';
      const response = method === 'delete' 
        ? await apiClient.delete(url)
        : await apiClient.patch(url, {});
      return response.data;
    },
    {
      onSuccess: (data, variables) => {
        const message = variables.hardDelete 
          ? 'Job alert permanently deleted'
          : 'Job alert moved to flagged';
        toast.success(message);
        queryClient.invalidateQueries('job-alerts');
        queryClient.invalidateQueries('job-alerts-stats');
        setDeleteDialog(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete job alert');
      },
    }
  );

  // Move to inactive (pause)
  const moveToInactiveMutation = useMutation(
    async (id) => {
      const response = await apiClient.patch(`/jobalerts/${id}/move-to-inactive`, {});
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Job alert moved to inactive');
        queryClient.invalidateQueries('job-alerts');
        queryClient.invalidateQueries('job-alerts-stats');
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to move job alert to inactive');
      },
    }
  );

  // Move to active (play) - using PATCH /unflag endpoint
  const moveToActiveMutation = useMutation(
    async (id) => {
      // For inactive alerts, use the unflag endpoint which sets is_active=true and is_deleted=false
      const response = await apiClient.patch(`/jobalerts/${id}/unflag`, {});
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Job alert moved to active');
        queryClient.invalidateQueries('job-alerts');
        queryClient.invalidateQueries('job-alerts-stats');
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to move job alert to active');
      },
    }
  );

  // Move to flagged
  const moveToFlaggedMutation = useMutation(
    async (id) => {
      const response = await apiClient.patch(`/jobalerts/${id}/move-to-flagged`, {});
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Job alert moved to flagged');
        queryClient.invalidateQueries('job-alerts');
        queryClient.invalidateQueries('job-alerts-stats');
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to move job alert to flagged');
      },
    }
  );

  // Unflag (move from flagged to active)
  const unflagMutation = useMutation(
    async (id) => {
      const response = await apiClient.patch(`/jobalerts/${id}/unflag`, {});
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Job alert unflagged and moved to active');
        queryClient.invalidateQueries('job-alerts');
        queryClient.invalidateQueries('job-alerts-stats');
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to unflag job alert');
      },
    }
  );

  // Permanent delete
  const permanentDeleteMutation = useMutation(
    async (id) => {
      const response = await apiClient.delete(`/jobalerts/${id}?hardDelete=true`);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Job alert permanently deleted');
        queryClient.invalidateQueries('job-alerts');
        queryClient.invalidateQueries('job-alerts-stats');
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to permanently delete job alert');
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      notificationName: '',
      organization: '',
      category: '',
      description: '',
      numberOfVacancies: 0,
      eligibility: '',
      qualification: '',
      ageLimit: '',
      salary: '',
      location: '',
      applicationStartDate: format(new Date(), 'yyyy-MM-dd'),
      lastDate: '',
      examDate: '',
      hallTicketDownloadDate: '',
      hallTicketDownloadStartingDate: '',
      resultsDate: '',
      applicationFee: '',
      officialWebsite: '',
      notificationUrl: '',
      requiredDocuments: [],
      onlineApplicationLink: '',
      importantDates: [],
      syllabus: [],
      applicationProcess: '',
      remarks: '',
      applicableFilters: [],
      isActive: false, // Default: Paused (hidden from users until admin plays)
      isUrgent: false,
    },
    validationSchema: editingAlert ? updateValidationSchema : createValidationSchema,
    onSubmit: (values) => {
      const submitValues = {
        ...values,
        numberOfVacancies: parseInt(values.numberOfVacancies) || 0,
        applicationStartDate: values.applicationStartDate || null,
        lastDate: values.lastDate || null,
        examDate: values.examDate || null,
        hallTicketDownloadStartingDate: values.hallTicketDownloadStartingDate || null,
        // Merge hall ticket info - preserve existing data, update downloadDate if provided
        hallTicketInfo: (() => {
          const existing = editingAlert?.hallTicketInfo || {};
          const existingObj = typeof existing === 'object' && !Array.isArray(existing) ? existing : {};
          return values.hallTicketDownloadDate 
            ? { ...existingObj, downloadDate: values.hallTicketDownloadDate, available: true }
            : existingObj;
        })(),
        // Merge result info - preserve existing data, update resultDate if provided
        resultInfo: (() => {
          const existing = editingAlert?.resultInfo || {};
          const existingObj = typeof existing === 'object' && !Array.isArray(existing) ? existing : {};
          return values.resultsDate
            ? { ...existingObj, resultDate: values.resultsDate, published: false }
            : existingObj;
        })(),
        requiredDocuments: Array.isArray(values.requiredDocuments)
          ? values.requiredDocuments
          : values.requiredDocuments.split(',').map((d) => d.trim()),
        importantDates: Array.isArray(values.importantDates)
          ? values.importantDates
          : values.importantDates.split(',').map((d) => d.trim()),
        syllabus: Array.isArray(values.syllabus)
          ? values.syllabus
          : values.syllabus.split(',').map((s) => s.trim()),
        applicableFilters: Array.isArray(values.applicableFilters)
          ? values.applicableFilters
          : values.applicableFilters.split(',').map((f) => f.trim()),
      };

      if (editingAlert) {
        updateMutation.mutate({ id: editingAlert.id, ...submitValues });
      } else {
        createMutation.mutate(submitValues);
      }
    },
    enableReinitialize: true,
  });

  const handleEdit = (alert) => {
    setEditingAlert(alert);
    formik.setValues({
      notificationName: alert.notificationName || '',
      organization: alert.organization || '',
      category: alert.category || '',
      description: alert.description || '',
      numberOfVacancies: alert.numberOfVacancies || 0,
      eligibility: alert.eligibility || '',
      qualification: alert.qualification || '',
      ageLimit: alert.ageLimit || '',
      salary: alert.salary || '',
      location: alert.location || '',
      applicationStartDate: alert.applicationStartDate
        ? format(new Date(alert.applicationStartDate), 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd'),
      lastDate: alert.lastDate
        ? format(new Date(alert.lastDate), 'yyyy-MM-dd')
        : '',
      examDate: alert.examDate ? format(new Date(alert.examDate), 'yyyy-MM-dd') : '',
      hallTicketDownloadStartingDate: alert.hallTicketDownloadStartingDate 
        ? format(new Date(alert.hallTicketDownloadStartingDate), 'yyyy-MM-dd') 
        : '',
      hallTicketDownloadDate: alert.hallTicketInfo?.downloadDate 
        ? format(new Date(alert.hallTicketInfo.downloadDate), 'yyyy-MM-dd') 
        : '',
      resultsDate: alert.resultInfo?.resultDate 
        ? format(new Date(alert.resultInfo.resultDate), 'yyyy-MM-dd') 
        : '',
      applicationFee: alert.applicationFee || '',
      officialWebsite: alert.officialWebsite || '',
      notificationUrl: alert.notificationUrl || '',
      requiredDocuments: Array.isArray(alert.requiredDocuments)
        ? alert.requiredDocuments.join(', ')
        : alert.requiredDocuments || '',
      onlineApplicationLink: alert.onlineApplicationLink || '',
      importantDates: Array.isArray(alert.importantDates)
        ? alert.importantDates.join(', ')
        : alert.importantDates || '',
      syllabus: Array.isArray(alert.syllabus) ? alert.syllabus.join(', ') : alert.syllabus || '',
      applicationProcess: alert.applicationProcess || '',
      remarks: alert.remarks || '',
      applicableFilters: Array.isArray(alert.applicableFilters)
        ? alert.applicableFilters.join(', ')
        : alert.applicableFilters || '',
      isActive: alert.isActive !== undefined ? alert.isActive : true,
      isUrgent: alert.isUrgent || false,
    });
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingAlert(null);
    formik.resetForm();
    setDialogOpen(true);
  };

  if (!data && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load job alerts. Please try again.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Job Alerts Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              refetch();
              queryClient.invalidateQueries('job-alerts');
              queryClient.invalidateQueries('job-alerts-stats');
              toast.info('Refreshing job alerts...');
            }}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/job-alerts/smart-create')}
            sx={{ textTransform: 'none' }}
          >
            Smart Create (AI)
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/job-alerts/subscribers/manage')}
            sx={{ textTransform: 'none' }}
          >
            Subscribers
          </Button>
          <Button
            variant="outlined"
            startIcon={<LinkIcon />}
            onClick={() => {
              const registrationUrl = `${window.location.origin}/user/job-alerts-registration`;
              navigator.clipboard.writeText(registrationUrl).then(() => {
                toast.success('Registration link copied to clipboard!');
              }).catch(() => {
                // Fallback: show in alert
                const link = prompt('Registration Link (copy this):', registrationUrl);
                if (link) {
                  navigator.clipboard.writeText(link);
                  toast.success('Link copied!');
                }
              });
            }}
            sx={{ textTransform: 'none' }}
          >
            Copy Registration Link
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/job-alerts/enquiries')}
            sx={{ textTransform: 'none' }}
          >
            View Enquiries
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            Create Job Alert
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Total Alerts
                </Typography>
                <Typography variant="h4">{stats.total || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Urgent Alerts
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.urgent || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Closing Soon
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.closingSoon || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  By Category
                </Typography>
                <Typography variant="h6">
                  {Object.keys(stats.byCategory || {}).length} Categories
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Card>
        <CardContent>
          {/* Status pages: Active / Inactive / Flagged / All — same activeFilter + pause/play/flag/unflag logic */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={activeFilter}
              onChange={(e, value) => {
                setActiveFilter(value);
                setPage(1);
              }}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="job alerts status tabs"
            >
              <Tab
                label={stats?.active != null ? `Active (${stats.active})` : 'Active'}
                value="active"
              />
              <Tab
                label={stats?.inactive != null ? `Inactive (${stats.inactive})` : 'Inactive'}
                value="inactive"
              />
              <Tab label="Flagged" value="flagged" />
              <Tab
                label={stats?.total != null ? `All (${stats.total})` : 'All'}
                value="all"
              />
            </Tabs>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              fullWidth
              sx={{ minWidth: 200, flex: 1 }}
              placeholder="Search job alerts..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1); // Reset to first page on search
              }}
            />
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Qualification / Eligibility</InputLabel>
              <Select
                value={qualificationFilter}
                label="Qualification / Eligibility"
                onChange={(e) => {
                  setQualificationFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">All Qualifications</MenuItem>
                {qualifications.map((qual) => (
                  <MenuItem key={qual.value} value={qual.value}>
                    {qual.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={departmentFilter}
                label="Department"
                onChange={(e) => {
                  setDepartmentFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">All Departments</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.value} value={dept.value}>
                    {dept.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Selection Bar - Visible when items are selected */}
          {selectedAlerts.size > 0 && (
            <Box 
              sx={{ 
                mb: 2,
                p: 2.5,
                bgcolor: 'error.light',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: 3,
                border: '2px solid',
                borderColor: 'error.main',
                position: 'sticky',
                top: 0,
                zIndex: 10
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <DeleteIcon sx={{ color: 'white', fontSize: 28 }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                  {selectedAlerts.size} Alert(s) Selected
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={<DeleteIcon />}
                onClick={() => handleDeleteClick(null)}
                disabled={deleting}
                sx={{ 
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  bgcolor: 'white',
                  color: 'error.main',
                  minWidth: 200,
                  '&:hover': {
                    bgcolor: 'error.dark',
                    color: 'white',
                    transform: 'scale(1.05)'
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(255, 255, 255, 0.5)',
                    color: 'rgba(0, 0, 0, 0.5)'
                  },
                  transition: 'all 0.2s'
                }}
              >
                {deleting ? 'Deleting...' : `Delete ${selectedAlerts.size} Selected`}
              </Button>
            </Box>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Tooltip title={selectAll ? "Deselect all matching alerts" : "Select all matching alerts (across all pages)"}>
                      <Checkbox
                        checked={selectAll}
                        indeterminate={selectedAlerts.size > 0 && selectedAlerts.size < (data?.total || 0)}
                        onChange={handleSelectAll}
                        color="primary"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>Notification / Online Update</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Last Date</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell>Vacancies / Slots</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Application Link</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.data?.map((alert) => {
                  const alertId = alert.id;
                  const isSelected = selectedAlerts.has(String(alertId));
                  return (
                  <TableRow key={alertId} hover selected={isSelected}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelectAlert(alertId)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WorkIcon color="primary" />
                        <Box>
                          <Typography fontWeight="medium">{alert.notificationName}</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                            {alert.isDeleted && (
                              <Chip label="Flagged" color="warning" size="small" />
                            )}
                            {alert.isUrgent && (
                              <Chip label="Urgent" color="error" size="small" />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{alert.organization}</TableCell>
                    <TableCell>
                      <Chip label={alert.category} color="primary" size="small" />
                    </TableCell>
                    <TableCell>
                      {alert.lastDate
                        ? format(new Date(alert.lastDate), 'MMM dd, yyyy')
                        : 'N/A'}
                    </TableCell>
                    <TableCell>{formatDateTimeWithSeconds(alert.createdAt || alert.created_at)}</TableCell>
                    <TableCell>{formatDateTimeWithSeconds(alert.updatedAt || alert.updated_at)}</TableCell>
                    <TableCell>{alert.numberOfVacancies || 'N/A'}</TableCell>
                    <TableCell align="center">
                      {(() => {
                        // Prefer tab filter; on "All" derive from row state
                        const rowStatus =
                          activeFilter === 'all'
                            ? (alert.isDeleted ? 'flagged' : alert.isActive ? 'active' : 'inactive')
                            : activeFilter;

                        if (rowStatus === 'flagged') {
                          return (
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Tooltip title="Unflag and move to active">
                                <IconButton
                                  size="small"
                                  onClick={() => unflagMutation.mutate(alert.id)}
                                  color="success"
                                  disabled={unflagMutation.isLoading}
                                >
                                  <PlayArrowIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Permanently delete">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (window.confirm(`Are you sure you want to permanently delete "${alert.notificationName}"? This action cannot be undone.`)) {
                                      permanentDeleteMutation.mutate(alert.id);
                                    }
                                  }}
                                  color="error"
                                  disabled={permanentDeleteMutation.isLoading}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          );
                        }

                        if (rowStatus === 'inactive') {
                          return (
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Tooltip title="Move to active (show to users)">
                                <IconButton
                                  size="small"
                                  onClick={() => moveToActiveMutation.mutate(alert.id)}
                                  color="success"
                                  disabled={moveToActiveMutation.isLoading}
                                >
                                  <PlayArrowIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Move to flagged">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (window.confirm(`Move "${alert.notificationName}" to flagged?`)) {
                                      moveToFlaggedMutation.mutate(alert.id);
                                    }
                                  }}
                                  color="warning"
                                  disabled={moveToFlaggedMutation.isLoading}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          );
                        }

                        return (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="Move to inactive (hide from users)">
                              <IconButton
                                size="small"
                                onClick={() => moveToInactiveMutation.mutate(alert.id)}
                                color="default"
                                disabled={moveToInactiveMutation.isLoading}
                              >
                                <PauseIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Move to flagged">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (window.confirm(`Move "${alert.notificationName}" to flagged?`)) {
                                    moveToFlaggedMutation.mutate(alert.id);
                                  }
                                }}
                                color="warning"
                                disabled={moveToFlaggedMutation.isLoading}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {alert.onlineApplicationLink ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LinkIcon />}
                          endIcon={<OpenInNewIcon />}
                          onClick={() => window.open(alert.onlineApplicationLink, '_blank')}
                          sx={{ textTransform: 'none' }}
                        >
                          Open Link
                        </Button>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No link
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => setViewDialog(alert)}>
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleEdit(alert)}>
                        <EditIcon />
                      </IconButton>
                      {/* Hide individual delete button when items are selected */}
                      {selectedAlerts.size === 0 && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(alert)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
                {data?.data?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No job alerts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {data?.total > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
              <Pagination
                count={Math.ceil((data?.total || 0) / 10)}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
          
          {/* Results count */}
          {data?.total !== undefined && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
              Showing {data?.data?.length || 0} of {data?.total || 0} job alerts
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>
            {editingAlert ? 'Edit Job Alert' : 'Create Job Alert'}
          </DialogTitle>
          <DialogContent dividers>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
              <Tab label="Basic Info" />
              <Tab label="Details" />
              <Tab label="Dates & Links" />
              <Tab label="Other" />
            </Tabs>

            {tabValue === 0 && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="notificationName"
                    name="notificationName"
                    label="Notification / Online Update"
                    value={formik.values.notificationName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.notificationName && Boolean(formik.errors.notificationName)
                    }
                    helperText={formik.touched.notificationName && formik.errors.notificationName}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="organization"
                    name="organization"
                    label="Organization"
                    value={formik.values.organization}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.organization && Boolean(formik.errors.organization)}
                    helperText={formik.touched.organization && formik.errors.organization}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Category</InputLabel>
                    <Select
                      id="category"
                      name="category"
                      value={formik.values.category}
                      onChange={formik.handleChange}
                      error={formik.touched.category && Boolean(formik.errors.category)}
                    >
                      {categories.map((cat) => (
                        <MenuItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="numberOfVacancies"
                    name="numberOfVacancies"
                    label="Vacancies / Slots"
                    type="number"
                    value={formik.values.numberOfVacancies}
                    onChange={formik.handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="description"
                    name="description"
                    label="Description"
                    multiline
                    rows={4}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    margin="normal"
                  />
                </Grid>
              </Grid>
            )}

            {tabValue === 1 && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="eligibility"
                    name="eligibility"
                    label="Eligibility"
                    value={formik.values.eligibility}
                    onChange={formik.handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="qualification"
                    name="qualification"
                    label="Qualification / Eligibility"
                    value={formik.values.qualification}
                    onChange={formik.handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="ageLimit"
                    name="ageLimit"
                    label="Age Limit"
                    value={formik.values.ageLimit}
                    onChange={formik.handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="salary"
                    name="salary"
                    label="Salary"
                    value={formik.values.salary}
                    onChange={formik.handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="location"
                    name="location"
                    label="Location"
                    value={formik.values.location}
                    onChange={formik.handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="applicationProcess"
                    name="applicationProcess"
                    label="Application Process"
                    multiline
                    rows={3}
                    value={formik.values.applicationProcess}
                    onChange={formik.handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="remarks"
                    name="remarks"
                    label="Description / Remarks"
                    multiline
                    rows={3}
                    value={formik.values.remarks}
                    onChange={formik.handleChange}
                    margin="normal"
                    helperText="Enter additional description or remarks"
                  />
                </Grid>
              </Grid>
            )}

            {tabValue === 2 && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    id="applicationStartDate"
                    name="applicationStartDate"
                    label="Start Date"
                    type="date"
                    value={formik.values.applicationStartDate}
                    onChange={formik.handleChange}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    id="lastDate"
                    name="lastDate"
                    label="Last Date"
                    type="date"
                    value={formik.values.lastDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.lastDate && Boolean(formik.errors.lastDate)}
                    helperText={formik.touched.lastDate && formik.errors.lastDate}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    id="examDate"
                    name="examDate"
                    label="Exam / Event Date"
                    type="date"
                    value={formik.values.examDate}
                    onChange={formik.handleChange}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    id="hallTicketDownloadStartingDate"
                    name="hallTicketDownloadStartingDate"
                    label="Hall Tickets D/L Starting Date"
                    type="date"
                    value={formik.values.hallTicketDownloadStartingDate}
                    onChange={formik.handleChange}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    helperText="Starting date when hall tickets can be downloaded"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    id="hallTicketDownloadDate"
                    name="hallTicketDownloadDate"
                    label="Hall Ticket Download Date"
                    type="date"
                    value={formik.values.hallTicketDownloadDate}
                    onChange={formik.handleChange}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    helperText="Date when hall ticket will be available"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    id="resultsDate"
                    name="resultsDate"
                    label="Results Date"
                    type="date"
                    value={formik.values.resultsDate}
                    onChange={formik.handleChange}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    helperText="Date when results will be published"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="applicationFee"
                    name="applicationFee"
                    label="Fee / Charges"
                    value={formik.values.applicationFee}
                    onChange={formik.handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="officialWebsite"
                    name="officialWebsite"
                    label="Official Website"
                    value={formik.values.officialWebsite}
                    onChange={formik.handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="notificationUrl"
                    name="notificationUrl"
                    label="Notification URL"
                    value={formik.values.notificationUrl}
                    onChange={formik.handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="onlineApplicationLink"
                    name="onlineApplicationLink"
                    label="Online Link / Booking Link"
                    value={formik.values.onlineApplicationLink}
                    onChange={formik.handleChange}
                    margin="normal"
                  />
                </Grid>
              </Grid>
            )}

            {tabValue === 3 && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="requiredDocuments"
                    name="requiredDocuments"
                    label="Required Documents (comma separated)"
                    value={formik.values.requiredDocuments}
                    onChange={formik.handleChange}
                    margin="normal"
                    helperText="Separate multiple documents with commas"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="importantDates"
                    name="importantDates"
                    label="Important Dates (comma separated)"
                    value={formik.values.importantDates}
                    onChange={formik.handleChange}
                    margin="normal"
                    helperText="Separate multiple dates with commas"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="syllabus"
                    name="syllabus"
                    label="Syllabus (comma separated)"
                    value={formik.values.syllabus}
                    onChange={formik.handleChange}
                    margin="normal"
                    helperText="Separate multiple topics with commas"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="applicableFilters"
                    name="applicableFilters"
                    label="Applicable Filters (comma separated)"
                    value={formik.values.applicableFilters}
                    onChange={formik.handleChange}
                    margin="normal"
                    helperText="Separate multiple filters with commas"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        id="isActive"
                        name="isActive"
                        checked={formik.values.isActive}
                        onChange={formik.handleChange}
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        id="isUrgent"
                        name="isUrgent"
                        checked={formik.values.isUrgent}
                        onChange={formik.handleChange}
                      />
                    }
                    label="Urgent"
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {createMutation.isLoading || updateMutation.isLoading ? (
                <CircularProgress size={24} />
              ) : editingAlert ? (
                'Update'
              ) : (
                'Create'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!viewDialog} onClose={() => setViewDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Job Alert Details</DialogTitle>
        <DialogContent dividers>
          {viewDialog && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {viewDialog.notificationName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {viewDialog.organization}
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Category
                  </Typography>
                  <Chip label={viewDialog.category} color="primary" size="small" />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Vacancies / Slots
                  </Typography>
                  <Typography variant="body1">{viewDialog.numberOfVacancies || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">{viewDialog.description || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    End / Last Date
                  </Typography>
                  <Typography variant="body1">
                    {viewDialog.lastDate
                      ? format(new Date(viewDialog.lastDate), 'MMM dd, yyyy')
                      : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={viewDialog.isActive ? 'Active' : 'Inactive'}
                    color={viewDialog.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Grid>
                {viewDialog.onlineApplicationLink && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Application Link
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<LinkIcon />}
                      endIcon={<OpenInNewIcon />}
                      onClick={() => window.open(viewDialog.onlineApplicationLink, '_blank')}
                      sx={{ textTransform: 'none' }}
                    >
                      Open Application Link
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(null)}>Close</Button>
          {viewDialog?.onlineApplicationLink && (
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              endIcon={<OpenInNewIcon />}
              onClick={() => window.open(viewDialog.onlineApplicationLink, '_blank')}
            >
              Open Link
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => !deleting && setDeleteDialog(null)}>
        <DialogTitle>
          {deleteDialog?.bulk 
            ? `${activeFilter === 'flagged' ? 'Permanently Delete' : 'Move to Flagged'} ${deleteDialog.count} Job Alert(s)?` 
            : activeFilter === 'flagged' 
              ? 'Permanently Delete Job Alert?' 
              : 'Move Job Alert to Flagged?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {deleteDialog?.bulk ? (
              <>
                {activeFilter === 'flagged' ? (
                  <>
                    Are you sure you want to <strong style={{ fontSize: '1.2em', color: '#d32f2f' }}>permanently delete</strong> <strong style={{ fontSize: '1.2em', color: '#d32f2f' }}>{deleteDialog.count}</strong> selected job alert(s)?
                    <br />
                    <br />
                    <strong>⚠️ This action cannot be undone. All {deleteDialog.count} selected alerts will be permanently deleted from the database.</strong>
                  </>
                ) : (
                  <>
                    Are you sure you want to move <strong style={{ fontSize: '1.2em', color: '#f57c00' }}>{deleteDialog.count}</strong> selected job alert(s) to <strong style={{ fontSize: '1.2em', color: '#f57c00' }}>flagged</strong>?
                    <br />
                    <br />
                    <strong>ℹ️ Flagged alerts can be restored later. They will be moved to the flagged section.</strong>
                  </>
                )}
                <br />
                <br />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  After the operation, you will see an alert showing exactly how many records were processed.
                </Typography>
              </>
            ) : (
              <>
                {activeFilter === 'flagged' ? (
                  <>
                    Are you sure you want to <strong style={{ color: '#d32f2f' }}>permanently delete</strong> job alert "{deleteDialog?.notificationName}"?
                    <br />
                    <br />
                    <strong>⚠️ This action cannot be undone. The job alert will be permanently removed from the database.</strong>
                  </>
                ) : (
                  <>
                    Are you sure you want to move job alert "{deleteDialog?.notificationName}" to <strong style={{ color: '#f57c00' }}>flagged</strong>?
                    <br />
                    <br />
                    <strong>ℹ️ Flagged alerts can be restored later. They will be moved to the flagged section.</strong>
                  </>
                )}
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color={activeFilter === 'flagged' ? 'error' : 'warning'}
            variant="contained"
            onClick={deleteDialog?.bulk 
              ? handleDeleteConfirm 
              : () => deleteMutation.mutate({ 
                  id: deleteDialog.id, 
                  hardDelete: activeFilter === 'flagged' 
                })}
            disabled={deleting || deleteMutation.isLoading}
            startIcon={(deleting || deleteMutation.isLoading) ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {(deleting || deleteMutation.isLoading) 
              ? (activeFilter === 'flagged' ? 'Deleting...' : 'Moving...') 
              : (activeFilter === 'flagged' ? 'Permanently Delete' : 'Move to Flagged')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobAlertsManagement;
